// Pure logic for the Advice Repository: form values, validation and summaries.
// No React/I/O so it is unit-tested. One advice record has up to five optional
// sections (see constants/tspAdvice.js); at least one must be written. These
// checks mirror the API's for a better UX — the server validates independently,
// and decides the author, the team and whether the journey/city are valid.
import { ADVICE_SECTIONS, ADVICE_SECTION_MAX, ADVICE_SECTION_MIN } from "../../constants/tspAdvice";

export const ADVICE_PAGE_SIZE = 8;

export const EMPTY_ADVICE_FORM = Object.freeze({
  journeyId: "",
  locationId: "",
  ...Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, ""])),
});

// Top-to-bottom on screen — used to focus the first invalid field.
export const ADVICE_FIELD_ORDER = ["journeyId", "locationId", ...ADVICE_SECTIONS.map((section) => section.key)];

const writtenSections = (values) => ADVICE_SECTIONS.filter((section) => values[section.key].trim() !== "");

// mode "new" also needs the journey and city; "edit" only the text.
export function validateAdvice(values, { mode = "new" } = {}) {
  const errors = {};
  if (mode === "new") {
    if (!values.journeyId) errors.journeyId = "Select the journey this advice comes from.";
    if (!values.locationId) errors.locationId = "Select the city.";
  }

  ADVICE_SECTIONS.forEach((section) => {
    const text = values[section.key].trim();
    if (!text) return;
    if (text.length > ADVICE_SECTION_MAX) errors[section.key] = `Must be ${ADVICE_SECTION_MAX} characters or fewer.`;
  });

  if (writtenSections(values).length === 0) errors.sections = "Write at least one section.";
  return errors;
}

// Whitelist the writable fields (never spread form state into a request).
// An emptied section is sent as null so an edit can clear it.
const sectionsBody = (values) => Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, values[section.key].trim() || null]));

export function buildCreateBody(values) {
  const sections = sectionsBody(values);
  return {
    journey_id: Number(values.journeyId),
    location_id: Number(values.locationId),
    ...Object.fromEntries(Object.entries(sections).filter(([, text]) => text !== null)),
  };
}

export const buildUpdateBody = (values) => sectionsBody(values);

export function adviceToFormValues(item) {
  return {
    journeyId: item.journey ? String(item.journey.id) : "",
    locationId: item.location ? String(item.location.id) : "",
    ...Object.fromEntries(ADVICE_SECTIONS.map((section) => [section.key, item.sections[section.key] || ""])),
  };
}

// First paragraph, cut on a word boundary.
export function summarize(content, max = 170) {
  const firstParagraph = content.split(/\n\s*\n/)[0].replace(/\s+/g, " ").trim();
  if (firstParagraph.length <= max) return firstParagraph;
  const cut = firstParagraph.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : max).replace(/[.,;:\s]+$/, "")}…`;
}

// The section that best introduces an advice record on a card: its first written one.
export function leadSection(item) {
  const section = ADVICE_SECTIONS.find((entry) => item.sections[entry.key]);
  return section ? { ...section, text: item.sections[section.key] } : null;
}
